import { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { IconCheck, IconAlertCircle } from "@tabler/icons-react"
import { PageContainer } from "@/components/PageContainer"
import { useSubscriptionStatus } from "@/data/hooks/api"
import { Button } from "@/components/ui/button"

const POLL_INTERVAL = 2000 // 2 seconds
const MAX_ATTEMPTS = 30 // 30 attempts = 60 seconds total

type CheckoutState = "polling" | "success" | "timeout"

export function CheckoutSuccessPage() {
  const navigate = useNavigate()
  const [state, setState] = useState<CheckoutState>("polling")
  const [planName, setPlanName] = useState<string | null>(null)
  const attemptsRef = useRef(0)

  const { data: status } = useSubscriptionStatus({
    enabled: state === "polling",
    refetchInterval: state === "polling" ? POLL_INTERVAL : false,
  })

  // Increment attempts counter on actual polling intervals
  useEffect(() => {
    if (state !== "polling") return

    const interval = setInterval(() => {
      attemptsRef.current += 1

      // Check for timeout
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState("timeout")
      }
    }, POLL_INTERVAL)

    return () => clearInterval(interval)
  }, [state])

  // Check subscription status when it updates
  useEffect(() => {
    if (state !== "polling" || !status) return

    if (status.is_active) {
      // Subscription is active
      setState("success")
      setPlanName(status.plan_name)
      return
    }
  }, [status, state])

  // Redirect to billing after success
  useEffect(() => {
    if (state === "success") {
      const timer = setTimeout(() => {
        navigate("/organization/billing", { replace: true })
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [state, navigate])

  return (
    <PageContainer>
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-[24px]">
        {state === "polling" && (
          <>
            <div className="relative">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#654EDA] border-r-transparent" />
            </div>
            <div className="text-center space-y-[8px]">
              <h2 className="text-[20px] font-medium text-[#08090A]">
                Confirming your subscription
              </h2>
              <p className="text-[13px] text-[#8A8F98]">
                Please wait while we verify your payment...
              </p>
            </div>
          </>
        )}

        {state === "success" && (
          <>
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-[#654EDA]/10">
              <IconCheck className="h-6 w-6 text-[#654EDA]" />
            </div>
            <div className="text-center space-y-[8px]">
              <h2 className="text-[20px] font-medium text-[#08090A]">
                Subscription activated!
              </h2>
              <p className="text-[13px] text-[#8A8F98]">
                {planName
                  ? `You're now subscribed to the ${planName} plan.`
                  : "Your subscription is now active."}
              </p>
              <p className="text-[12px] text-[#8A8F98]">
                Redirecting to billing...
              </p>
            </div>
          </>
        )}

        {state === "timeout" && (
          <>
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-amber-100">
              <IconAlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <div className="text-center space-y-[8px]">
              <h2 className="text-[20px] font-medium text-[#08090A]">
                Still processing
              </h2>
              <p className="text-[13px] text-[#8A8F98] max-w-[320px]">
                Your subscription is being processed. This may take a few more moments.
                Please refresh the page or check back shortly.
              </p>
            </div>
            <Button
              variant="default"
              onClick={() => navigate("/organization/billing", { replace: true })}
            >
              Go to Billing
            </Button>
          </>
        )}
      </div>
    </PageContainer>
  )
}
